import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReturnButton from "./ReturnButton";
import { supabase } from "./supabaseClient";

export default function Subscription() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setError(null);
    setLoading(false);
    window.scrollTo(0, 0);
  }, [location.key]);

  // ✅ Abonnement Royal (2€ / 14 jours)
  const handleSubscribe = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await supabase.auth.getUser();
      const email = data?.user?.email;
      if (!email) throw new Error("Vous devez être connecté pour souscrire.");

      const resp = await fetch(
        "https://airnerilsoslbjmivezu.supabase.co/functions/v1/create-checkout-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || "Erreur Stripe.");
      window.location.href = result.url;
    } catch (err) {
      setError(err?.message || "Erreur inconnue");
      setLoading(false);
    }
  };

  // ✅ One-shot 10€ (sans abonnement) => prolonge l'accès de +3 mois côté webhook
  const handleOneShot10 = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await supabase.auth.getUser();
      const email = data?.user?.email;
      if (!email) throw new Error("Vous devez être connecté pour acheter le pack.");

      const resp = await fetch(
        "https://airnerilsoslbjmivezu.supabase.co/functions/v1/create-checkout-session-oneshot-10",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || "Erreur Stripe.");
      window.location.href = result.url;
    } catch (err) {
      setError(err?.message || "Erreur inconnue");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <ReturnButton />

      <h2 className="text-3xl font-bold mb-2 text-center">Choisir mon offre</h2>

      <p className="text-sm text-center max-w-3xl mb-4 text-gray-600">
        Abonnement ou paiement unique — aucun engagement.
      </p>

      <p className="text-sm text-center max-w-3xl mb-8 text-gray-600">
        En validant votre paiement, vous acceptez pleinement nos{" "}
        <a href="/cgv" target="_blank" rel="noopener noreferrer" className="underline">
          Conditions Générales de Vente (CGV)
        </a>{" "}
        et{" "}
        <a href="/cgu" target="_blank" rel="noopener noreferrer" className="underline">
          Conditions Générales d’Utilisation (CGU)
        </a>
        .
      </p>

      {/* ✅ 2 offres (2 cartes) */}
      <div className="flex flex-col md:flex-row gap-10 items-stretch justify-center w-full max-w-5xl">
        {/* === OFFRE 1 : ABONNEMENT ROYAL === */}
        <div className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center w-full md:w-1/2 border-2 border-blue-600">
          <h3 className="text-2xl font-bold mb-2">Royal</h3>

          <div className="text-4xl font-extrabold text-blue-700 mb-2">
            2€
            <span className="text-lg font-normal text-gray-500"> / 14 jours</span>
          </div>

          <div className="text-blue-500 text-lg font-bold mb-2">
            3 000 republications incluses
          </div>

          <p className="text-gray-500 mb-4 text-center">
            Abonnement flexible pour vendre plus, sans prise de tête.
          </p>

          <ul className="mb-6 space-y-2 text-sm text-gray-700 text-left w-full">
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Renouvellement automatique
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Annulable à tout moment
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Fin d’accès : 3 000 republications OU 14 jours
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Utilisable sur tous vos comptes Vinted
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Support email
            </li>
          </ul>

          <button
            onClick={handleSubscribe}
            disabled={loading}
            className={`w-full py-3 rounded-xl text-lg font-bold transition ${
              loading
                ? "bg-blue-400 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white shadow-xl"
            }`}
          >
            {loading ? "Redirection…" : "S’abonner"}
          </button>
        </div>

        {/* === OFFRE 2 : PACK ONE-SHOT 10€ === */}
        <div className="bg-white rounded-3xl shadow-xl p-8 flex flex-col items-center w-full md:w-1/2 border-2 border-green-600">
          <h3 className="text-2xl font-bold mb-2">Pack 3 mois</h3>

          <div className="text-4xl font-extrabold text-green-600 mb-2">
            10€
            <span className="text-lg font-normal text-gray-500"> / 3 mois sans abonnement</span>
          </div>

          <div className="text-green-500 text-lg font-bold mb-2">6000 republications incluses</div>

          <p className="text-gray-500 mb-4 text-center">
            Idéal si tu veux du temps sans abonnement ni renouvellement.
          </p>

          <ul className="mb-6 space-y-2 text-sm text-gray-700 text-left w-full">
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Aucun abonnement
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Aucun renouvellement
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Fin d’accès : 6 000 republications OU 3 mois
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Utilisable sur tous vos comptes Vinted
            </li>
            <li className="flex items-center">
              <span className="text-green-500 mr-2">✔</span> Support email
            </li>
          </ul>

          <button
            onClick={handleOneShot10}
            disabled={loading}
            className={`w-full py-3 rounded-xl text-lg font-bold transition ${
              loading
                ? "bg-green-200 text-green-800"
                : "bg-green-600 hover:bg-green-700 text-white shadow-xl"
            }`}
          >
            {loading ? "Redirection…" : "Acheter le pack"}
          </button>
        </div>
      </div>

      {error && <div className="mt-6 text-red-600 text-sm">{error}</div>}

      <div className="mt-10 text-sm text-gray-500 text-center max-w-3xl">
        L’abonnement se renouvelle automatiquement. Résiliation possible à tout moment (effet à la fin de la période en cours).
        <br />
        Le quota est réinitialisé à 3 000 à chaque reconduction — <b>pas de report</b>.
      </div>
    </div>
  );
}
